import { CognitoUserPool, AuthenticationDetails, CognitoUserAttribute, CognitoUser } from 'amazon-cognito-identity-js'
import { CognitoEventBus } from './events'

export default (AWS_COGNITO_POOL_ID, AWS_COGNITO_CLIENT_ID) => {
  return {
    /*
    ---------------------------------------------------------------------------------------------------------
    Initialize Auth (to be run when app first boots, to emit whatever user is stored in local storage)
    ---------------------------------------------------------------------------------------------------------
     */
    async init () {
      const user = await this.cognitoAuthenticatedUser()
      this.emitAuth(user)
      return user
    },

    /*
    ---------------------------------------------------------------------------------------------------------
    Login
    ---------------------------------------------------------------------------------------------------------
     */
    /**
     * Run login Mutation
     * @param email
     * @param password
     * @param mfaCodeCallback
     * @param newPasswordRequiredCallback
     * @param newPasswordAttributesCallback
     * @return {Promise}
     */
    login (email, password, mfaCodeCallback, newPasswordRequiredCallback, newPasswordAttributesCallback) {
      return new Promise(async (resolve, reject) => {
        // login with Cognito. AWS will automatically set the tokens to our local storage
        this.cognitoAuth(email, password, mfaCodeCallback, newPasswordRequiredCallback, newPasswordAttributesCallback)
          .then(() => {
            // after a successful login, resolve the promise with the authenticated user
            this.cognitoAuthenticatedUser()
              .then((user) => {
                // emit the user
                this.emitAuth(user)
                // resolve the user
                resolve(user)
              })
              .catch(reject)
          })
          .catch(reject)
      })
    },

    /**
     * Logout
     */
    async logout () {
      // logout of Cognito
      await this.cognitoSignOut()
      // emit the event
      this.emitAuth(null)
    },

    /**
     * Sign up new User
     * @param email
     * @param password
     * @return {Promise<any>}
     */
    async signUp (email, password) {
      try {
        const user = await this.cognitoSignUp(email, password)
        return user
      } catch (e) {
        throw e
      }
    },

    /**
     * Emit Auth event
     * @param user
     */
    emitAuth (user) {
      CognitoEventBus.$emit('auth', user)
    },

    /**
     * Listen for an auth event
     * @param callback
     */
    onAuth (callback) {
      CognitoEventBus.$on('auth', callback)
    },

    /*
    ---------------------------------------------------------------------------------------------------------
    Forgot Password
    ---------------------------------------------------------------------------------------------------------
     */
    /**
     * Send a Password Reset request
     * @param email
     * @return {Promise}
     */
    forgotPassword (email) {
      return new Promise((resolve, reject) => {
        // generate the Cognito user
        const cognitoUser = this.cognitoUser(email)
        // send the forgot password
        cognitoUser.forgotPassword({
          onSuccess: resolve,
          onFailure: reject
        })
      })
    },

    /**
     * Reset Password with Cognito verification code
     * @param email
     * @param verificationCode
     * @param newPassword
     * @return {Promise<any>}
     */
    resetPassword (email, verificationCode, newPassword) {
      return new Promise((resolve, reject) => {
        // generate the cognito user
        const cognitoUser = this.cognitoUser(email)
        // send the forgot password
        cognitoUser.confirmPassword(verificationCode, newPassword, {
          onSuccess: resolve,
          onFailure: reject
        })
      })
    },

    /**
     * Change the password for the currently logged in user
     * @param oldPassword
     * @param newPassword
     * @return {Promise<any>}
     */
    changePassword (oldPassword, newPassword) {
      return new Promise(async (resolve, reject) => {
        // get the current Cognito user
        const cognitoUser = await this.cognitoAuthenticatedUser()
        // change the password
        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            return reject(Error(err.message))
          }
          resolve(result)
        })
      })
    },

    /*
    ---------------------------------------------------------------------------------------------------------
    AWS Cognito
    ---------------------------------------------------------------------------------------------------------
     */
    cognitoPool () {
      return new CognitoUserPool({
        UserPoolId: AWS_COGNITO_POOL_ID,
        ClientId: AWS_COGNITO_CLIENT_ID
      })
    },

    /**
     * Create a new CognitoUser instance
     * @param username
     * @return {module:amazon-cognito-identity-js.CognitoUser}
     */
    cognitoUser (username) {
      const userPool = this.cognitoPool()
      return new CognitoUser({
        Username: username,
        Pool: userPool
      })
    },

    /**
     * Get Authenticated Cognito user
     * @return {Promise<null>}
     */
    async cognitoAuthenticatedUser () {
      const cognitoUser = this.cognitoPool().getCurrentUser()
      if (cognitoUser) {
        // fetch the session for this user, to insure that it's still valid
        try {
          const isValidSession = await this.cognitoSession(cognitoUser)
          if (isValidSession) {
            return cognitoUser
          }
          return null
        } catch (e) {
          return null
        }
      }
      return null
    },

    /**
     * Get the current Cognito user from localStorage
     * @param cognitoUser
     * @param includeInvalid - return the session data, even if it's invalid (will not attempt to refresh the token)
     * @return {Promise<any>}
     */
    cognitoSession (cognitoUser, includeInvalid) {
      return new Promise(async (resolve, reject) => {
        // if there is no user, return now
        if (!cognitoUser) {
          return resolve(null)
        }
        // otherwise check if the session is still valid
        cognitoUser.getSession((err, session) => {
          if (err) {
            return reject(err)
          }
          // if the session is still valid return the user
          if (includeInvalid || session.isValid()) {
            resolve(session)
          } else {
            // attempt to refresh the session
            this.cognitoRefreshSession()
              .then((newSession) => {
                resolve(newSession)
              })
              .catch((e) => {
                resolve(null)
              })
          }
        })
      })
    },

    /**
     * Attempt to refresh the Cognito session
     * @return {Promise<any>}
     */
    cognitoRefreshSession () {
      return new Promise(async (resolve, reject) => {
        const refreshToken = await this.cognitoRefreshToken()
        if (!refreshToken) {
          return reject(Error('No session exists to refresh'))
        }
        const cognitoUser = this.cognitoPool().getCurrentUser()
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            return reject(err)
          }
          resolve(session)
        })
      })
    },

    /**
     * Return the JWT token from the cached Cognito user
     * @returns {String}
     */
    async cognitoJwtToken () {
      const cognitoUser = this.cognitoPool().getCurrentUser()
      const session = await this.cognitoSession(cognitoUser)
      if (session && session.accessToken) {
        return session.accessToken.jwtToken
      }
      return null
    },

    /**
     * Return the refresh token for the cached Cognito user
     * @return {Promise<*>}
     */
    async cognitoRefreshToken () {
      const cognitoUser = this.cognitoPool().getCurrentUser()
      const session = await this.cognitoSession(cognitoUser, true)
      return session ? session.refreshToken || null : null
    },

    /**
     * Authenticate the user in Cognito
     * @param username
     * @param password
     * @param mfaCodeCallback
     * @param newPasswordRequiredCallback
     * @return {Promise<any>}
     */
    cognitoAuth (username, password, mfaCodeCallback, newPasswordRequiredCallback) {
      return new Promise((resolve, reject) => {
        // create the pool and user instances
        const authenticationDetails = new AuthenticationDetails({
          Username: username,
          Password: password
        })
        // create a user
        const cognitoUser = this.cognitoUser(username)
        // authenticate the user
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: resolve,
          onFailure: reject,

          // if MFA is required, then call the appopriate callback to the get the MFA code.
          // the callback will be passed the codeDeliveryDetails and a "done" function as the final argument,
          // which can be used to try and submit the code to Cognito (and handle any errors)
          mfaRequired: async (codeDeliveryDetails) => {
            const parentResolve = resolve
            // call the callback
            mfaCodeCallback(codeDeliveryDetails, (mfaCode) => {
              // submit the code
              return new Promise((resolve, reject) => {
                cognitoUser.sendMFACode(mfaCode, {
                  onSuccess: () => {
                    resolve()
                    parentResolve()
                  },
                  onFailure: (err) => {
                    reject(err)
                  }
                })
              })
            })
          },

          // if a new password is required, then call the appropriate callback.
          // the callback will be passed the attributes from Cognito and a "done" function as the final argument,
          // which can be used to try and submit the new password to Cognito (and handle any errors).
          newPasswordRequired: async (userAttributes, requiredAttributes) => {
            const parentResolve = resolve
            // send the attributes to the callback function with the last parameter as the done method
            newPasswordRequiredCallback(userAttributes, requiredAttributes, (password, attributesData) => {
              // submit the new password
              return new Promise((resolve, reject) => {
                cognitoUser.completeNewPasswordChallenge(password, attributesData, {
                  onSuccess: () => {
                    resolve()
                    parentResolve()
                  },
                  onFailure: (err) => {
                    reject(err)
                  }
                })
              })
            })
          }
        })
      })
    },

    /**
     * Sign Up with Cognito
     * @param email
     * @param password
     * @return {Promise<any>}
     */
    cognitoSignUp (email, password) {
      return new Promise((resolve, reject) => {
        const userPool = this.cognitoPool()
        // build attributes
        const attributeList = []
        attributeList.push(new CognitoUserAttribute({
          Name: 'email',
          Value: email
        }))
        // sign up
        const username = email
        userPool.signUp(username, password, attributeList, null, (err, result) => {
          if (err) {
            return reject(err)
          }
          resolve(result.user)
        })
      })
    },

    /**
     * Confirm an Amazon Cognito registration
     * @param username
     * @param code
     * @return {Promise<any>}
     */
    cognitoConfirmRegistration (username, code) {
      return new Promise(async (resolve, reject) => {
        // create a CognitoUser instance
        const cognitoUser = this.cognitoUser(username)
        // resend the code
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            return reject(err)
          }
          resolve(result)
        })
      })
    },

    /**
     * Sign out of Cognito
     * @return {Promise<void>}
     */
    async cognitoSignOut () {
      const user = this.cognitoPool().getCurrentUser()
      if (user) {
        user.signOut()
      }
    },

    /**
     * Resend the Confirmation Code to the user whose credentials are currently in localStorage (the user who last tried to login)
     * @return {Promise<*>}
     */
    cognitoResendConfirmationCode (username) {
      return new Promise(async (resolve, reject) => {
        // create a CognitoUser instance
        const cognitoUser = this.cognitoUser(username)
        // resend the code
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            return reject(err)
          }
          resolve(result)
        })
      })
    }
  }
}
